import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { fetchCreators } from "@/lib/queries";
import { CreatorSkeleton } from "@/components/skeleton-card";
import { resolveImage } from "@/lib/image-map";
import { Reveal } from "@/components/reveal";
import { TextGenerateEffect } from "@/components/text-generate-effect";
import { VerifiedBadge } from "@/components/verified-badge";
import { useState } from "react";

export const Route = createFileRoute("/creators")({
  head: () => ({
    meta: [
      { title: "Meet Our Creators — Shop Influencer Curations | Pluggz" },
      { name: "description", content: "Discover verified UK creators on Pluggz. Shop their curated collections across fashion, beauty, home, travel and more." },
      { property: "og:title", content: "Meet Our Creators — Shop Influencer Curations | Pluggz" },
      { property: "og:description", content: "Discover verified UK creators on Pluggz. Shop their curated collections across fashion, beauty, home, travel and more." },
      { property: "og:url", content: `${SITE_URL}/creators` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/creators` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Pluggz Creators Directory",
          description: "Directory of vetted creators running curated storefronts on Pluggz.",
          url: `${SITE_URL}/creators`,
        }),
      },
    ],
  }),
  component: Creators,
});

function Creators() {
  const { data: creators = [], isLoading } = useQuery({ queryKey: ["creators"], queryFn: () => fetchCreators() });
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const visible = verifiedOnly ? creators.filter((c) => c.is_verified) : creators;
  return (
    <div className="mx-auto max-w-7xl px-6 pt-12 pb-24">
      <Reveal>
        <p className="editorial-eyebrow">The Roster</p>
        <h1 className="mt-2 font-serif text-5xl">
          Our <span className="italic shimmer">Creators</span>
        </h1>
        <TextGenerateEffect
          words="Vetted editors, stylists, and tastemakers. Each one runs their own storefront."
          className="mt-3 max-w-xl text-muted-foreground"
          duration={0.4}
          staggerDelay={0.04}
        />
      </Reveal>

      <div className="mt-8 flex items-center gap-3">
        <button
          type="button"
          aria-pressed={verifiedOnly}
          onClick={() => setVerifiedOnly((v) => !v)}
          className={
            "inline-flex items-center gap-2 border px-3 py-1.5 text-xs uppercase tracking-widest transition " +
            (verifiedOnly
              ? "border-foreground bg-foreground text-background"
              : "border-border text-muted-foreground hover:text-foreground")
          }
        >
          <VerifiedBadge />
          Verified only
        </button>
      </div>

      <div className="mt-14 grid gap-10 md:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          <>
            <CreatorSkeleton />
            <CreatorSkeleton />
            <CreatorSkeleton />
            <CreatorSkeleton />
            <CreatorSkeleton />
            <CreatorSkeleton />
          </>
        ) : (
          visible.map((c, i) => (
            <Reveal key={c.handle} delay={(i % 3) * 120}>
              <Link to="/creator/$handle" params={{ handle: c.handle }} className="group card-lift block border border-border bg-card overflow-hidden rounded-sm">
                <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                  <img src={resolveImage(c.cover_url)} alt={c.name} loading="lazy" className="ken-burns h-full w-full object-cover" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={resolveImage(c.avatar_url)} alt={c.name} className="h-12 w-12 rounded-full object-cover ring-1 ring-border" />
                    <div>
                      <p className="font-serif text-xl flex items-center gap-1.5">
                        {c.name}
                        {c.is_verified && <VerifiedBadge />}
                      </p>
                      <p className="text-xs text-muted-foreground">@{c.handle} · {c.followers}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground">{c.bio}</p>
                </div>
              </Link>
            </Reveal>
          ))
        )}
      </div>
      {!isLoading && visible.length === 0 && (
        <Reveal>
          <div className="mx-auto mt-20 max-w-md text-center">
            <p className="editorial-eyebrow">Coming soon</p>
            <h2 className="mt-3 font-serif text-4xl">{verifiedOnly ? "No verified creators yet" : "Our first creators are being selected"}</h2>
            <p className="mt-4 text-sm text-muted-foreground">
              {verifiedOnly ? "Try turning off the verified filter." : "Applications are open — be among the first."}
            </p>
            <Link
              to="/apply"
              className="mt-8 inline-flex items-center border border-foreground/80 px-5 py-2.5 text-xs uppercase tracking-widest text-foreground transition-colors hover:bg-foreground hover:text-background"
            >
              Apply now
            </Link>
          </div>
        </Reveal>
      )}
    </div>
  );
}
